<template>
  <div class="page">
    <div class="sm:mx-auto sm:w-full sm:max-w-full">
      <Card>
        <CardContent v-if="loading">
          <InterviewSkeleton />
        </CardContent>

        <CardContent v-else>
          <div class="md:grid md:grid-cols-12 md:divide-x md:divide-gray-200">
            <InterviewDetails :team="team" :existing-event="existingEvent" :rescheduling="reschedulingEvent" />

            <template v-if="errorLabel">
              <div class="status-panel">
                <Alert variant="info" class="max-w-md py-6">
                  <div class="flex gap-2">
                    <vue-feather type="calendar" class="h-4 w-4" />
                    <AlertTitle class="mb-3">Online scheduling is not currently available</AlertTitle>
                  </div>
                  <AlertDescription>
                    {{ errorLabel }}
                  </AlertDescription>
                </Alert>
              </div>
            </template>

            <template v-else>
              <InterviewCalendar :event-slots="eventSlots" @select-day="changeDay" />
              <InterviewBooking
                :slots="currentDaySlots"
                :current-day="currentDay"
                :candidate="candidate"
                :rescheduling="reschedulingEvent"
                :editable="route.query.event_type == 'humaniz_checkin'"
                :booking="booking"
                @confirm="createEvent"
              />
            </template>
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { toast } from 'vue-sonner';
import { Card, CardContent } from '@/components/ui/card';
import InterviewSkeleton from './InterviewSkeleton.vue';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import InterviewCalendar from './InterviewCalendar.vue';
import InterviewDetails from './InterviewDetails.vue';
import InterviewBooking from './InterviewBooking.vue';
import { useInterviewBooking, type EventSlot } from './useInterviewBooking';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import DateUtility from '../../utilities/dates';
import { Api } from '@/api';
import type { ContactEventType } from '@/api/entity_types/ContactEvent';

const route = useRoute();
const loadingSpinnerStore = useLoadingSpinnerStore();

const { formatDate, isDateSame } = DateUtility;

const team = ref({
  companyLogo: '',
  name: '',
  eventName: '',
  eventDescription: '',
});

const candidate = ref({
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
});

const existingEvent = ref({
  eventName: '',
  startTime: '',
  endTime: '',
});

const booking = useInterviewBooking();

const reschedulingEvent = ref(false);
const currentDay = ref(new Date());
const eventSlots = ref<EventSlot[]>([]);
const loading = ref(false);
const errorLabel = ref('');

const currentDaySlots = computed(() => {
  return eventSlots.value.filter(slot => isDateSame(new Date(slot.startTime), currentDay.value, 'day'));
});

const changeDay = (date: Date) => {
  currentDay.value = date;
  booking.reset();
};

const getTeam = async () => {
  const response = await Api.Public.ContactInterview.getTeam({
    id: route.params.id as string,
    event_type: route.query.event_type as ContactEventType,
    contact_id: route.query.contact_id as string | undefined,
    humaniz_checkin_user_id: route.query.humaniz_checkin_user_id as string | undefined,
  });

  if (response.status !== 'success') return;

  const data = response.data;

  team.value.name = data.company;
  team.value.eventName = data.event_name;
  team.value.eventDescription = data.event_description;
  team.value.companyLogo = data.company_logo ?? '';

  if (data.contact) {
    candidate.value.firstName = data.contact.first_name ?? '';
    candidate.value.lastName = data.contact.last_name ?? '';
    candidate.value.email = data.contact.email ?? '';
    candidate.value.phone = data.contact.phone_number ?? '';
  }
  if (data.existing_event) {
    existingEvent.value.eventName = data.existing_event.name;
    existingEvent.value.startTime = data.existing_event.start_time;
    existingEvent.value.endTime = data.existing_event.end_time;
    reschedulingEvent.value = true;
  }
};

const getEventSlots = async () => {
  const response = await Api.Public.ContactInterview.getEventSlots({
    id: route.params.id as string,
    event_type: route.query.event_type as ContactEventType,
    contact_id: route.query.contact_id as string | undefined,
    humaniz_checkin_user_id: route.query.humaniz_checkin_user_id as string | undefined,
  });

  if (response.status === 'success') {
    eventSlots.value = response.data.map(slot => ({
      startTime: slot.start_time,
      endTime: slot.end_time,
      id: `${formatDate(slot.start_time, 'MMMM dd, h:mm a')} - ${formatDate(slot.end_time, 'h:mm a')}`,
    }));
  } else {
    errorLabel.value = response.messages.join(', ');
  }
};

const createEvent = async (startTime: string, endTime: string) => {
  loadingSpinnerStore.showLocalLoadingSpinner();

  const response = await Api.Public.ContactInterview.createContactInterview({
    event_type: route.query.event_type as ContactEventType,
    contact_id: route.query.contact_id as string,
    humaniz_checkin_user_id: route.query.humaniz_checkin_user_id as string,
    company_slug: route.params.id as string,
    start_time: startTime,
    end_time: endTime,
    contact: candidate.value,
    scheduled_by: 'public',
  });

  loadingSpinnerStore.hideLocalLoadingSpinner();

  if (response.status === 'success') {
    toast.success('Event successfully scheduled!', {
      action: { label: 'Dismiss' },
      description: 'Your event has been successfully scheduled. Use the button below to dismiss this alert.',
    });
    booking.reset();
    reloadData();
  } else {
    toast.error('Event schedule error', {
      description: response.messages.join(', '),
    });
  }
};

const reloadData = async () => {
  loading.value = true;
  try {
    await Promise.all([getTeam(), getEventSlots()]);
  } catch {
    // Network error — prevents unhandled rejection
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  reloadData();
});
</script>

<style scoped>
@reference 'tailwindcss';

.page {
  @apply min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 overflow-y-scroll;
}

.status-panel {
  @apply md:col-span-8 mt-12 md:mt-0 md:pl-14 flex items-center justify-center;
}
</style>
